import { useEffect, useState } from "react";
import api from "@/lib/api";
import { APPT_MODES, APPT_TYPES } from "@/lib/crm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useApptAvailability, ApptConflictBanner } from "@/components/crm/useApptAvailability";

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

export function EditAppointmentModal({ appt, open, onClose, onDone, extraActions }) {
  const [dt, setDt] = useState("");
  const [duration, setDuration] = useState("30");
  const [mode, setMode] = useState("Telefonico");
  const [note, setNote] = useState("");
  const [tipologia, setTipologia] = useState("");
  const [saving, setSaving] = useState(false);
  const { data: avail } = useApptAvailability(dt, duration, appt?.id);
  const hasConflict = !!avail && (avail.conflicts?.length > 0 || avail.google_conflicts?.length > 0);

  useEffect(() => {
    if (appt && open) {
      setDt(toLocalInput(appt.start_at));
      setDuration(String(appt.duration || 30));
      setMode(appt.mode || "Telefonico");
      setNote(appt.note || "");
      setTipologia(appt.tipologia || "");
    }
  }, [appt, open]);

  const save = async (force = false) => {
    if (!dt) { toast.error("Seleziona data e ora"); return; }
    setSaving(true);
    try {
      await api.put(`/appointments/${appt.id}`, {
        start_at: new Date(dt).toISOString(), duration: parseInt(duration), mode, note, tipologia: tipologia || undefined, force,
      });
      setSaving(false);
      toast.success("Appuntamento aggiornato");
      onClose(); onDone && onDone();
    } catch (e) {
      setSaving(false);
      if (e.response?.status === 409) {
        if (window.confirm("⚠️ Hai già un appuntamento in questa fascia oraria. Procedere comunque?")) save(true);
      } else toast.error(e.response?.data?.detail || "Errore");
    }
  };

  if (!appt) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="edit-appointment-modal">
        <DialogHeader><DialogTitle className="font-heading">Modifica appuntamento</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label className="text-xs">Data e ora</Label>
            <Input data-testid="edit-appt-datetime" type="datetime-local" value={dt} onChange={(e) => setDt(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs">Durata (min)</Label>
              <Select value={duration} onValueChange={setDuration}>
                <SelectTrigger data-testid="edit-appt-duration"><SelectValue /></SelectTrigger>
                <SelectContent>{["15", "30", "45", "60", "90"].map((d) => <SelectItem key={d} value={d}>{d} min</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label className="text-xs">Modalità</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger data-testid="edit-appt-mode"><SelectValue /></SelectTrigger>
                <SelectContent>{APPT_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1"><Label className="text-xs">Tipologia</Label>
            <Select value={tipologia} onValueChange={setTipologia}>
              <SelectTrigger data-testid="edit-appt-tipologia"><SelectValue placeholder="Non specificata" /></SelectTrigger>
              <SelectContent>
                {APPT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label className="text-xs">Nota</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nota…" rows={2} data-testid="edit-appt-note" />
          </div>
          <ApptConflictBanner data={avail} />
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          {extraActions ? <div className="flex gap-2 mr-auto">{extraActions}</div> : <span />}
          <Button data-testid="edit-appt-save" onClick={() => save(hasConflict)} disabled={saving}
            className={hasConflict ? "bg-amber-600 hover:bg-amber-700" : ""}>
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{hasConflict ? "Salva comunque" : "Salva modifiche"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default EditAppointmentModal;
